import { useEffect, useRef, useState } from 'react';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast, Slide } from 'react-toastify';
import { debounce } from 'loadsh';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import LocaleSelect from './locale-select';
import { useLocale } from '../../lib/context/locale-context';

export default function SiteNav() {
  const { getLocale, localePrefix } = useLocale();

  const { pathname, hash } = useLocation();

  const [click, setClick] = useState(false);
  const shareRef = useRef(null);

  const [mWidth, setWidth] = useState(window.innerWidth);
  const [mHeight, setHeight] = useState(window.innerHeight);

  const [active, setActive] = useState(null);

  // nav 높이 고정 (web>=1280: 69px, 그 이하는 60px)
  const navHeightClass = 'h-[69px]';
  const navItemSlotClass = 'w-[69px] h-[69px]';
  useEffect(() => {
    // 홈 섹션 해시(#2~#5)일 때만 active 처리
    if (pathname.startsWith(`${localePrefix}/home`) && hash) {
      const id = hash.replace('#', '');
      setActive(id);
      return;
    }
    setActive(null);
  }, [hash, pathname, localePrefix]);

  useEffect(() => {
    // 스크롤에 따라 현재 섹션 active 갱신 (home 전용)
    if (!pathname.startsWith(`${localePrefix}/home`)) return;

    const root = document.getElementById('fullPage') ?? null;
    const targets = ['0','2', '3', '4', '5']
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (targets.length === 0) return;

    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.target?.id) continue;
          ratios.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }

        let bestId = null;
        let bestRatio = 0;
        for (const [id, ratio] of ratios.entries()) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }

        if (bestId) setActive(bestId);
      },
      {
        root,
        // 스냅 스크롤에서도 안정적으로 잡히도록 여러 임계값 사용
        threshold: [0.25, 0.4, 0.55, 0.7],
      },
    );

    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [pathname]);

  const notify = () => {
    toast('복사가 완료되었습니다.', {
      position: 'bottom-center',
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      progress: undefined,
      theme: 'dark',
      transition: Slide,
    });
    toast.clearWaitingQueue();
  };

  const handleToggleClick = () => setClick((prev) => !prev);

  const handleResize = debounce(() => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
    document.documentElement.style.setProperty('--h', `${window.innerHeight / 100}px`);
  }, 300);

  useEffect(() => {
    handleResize();

    const handleClickOutSide = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setClick(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutSide);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutSide);
    };
  }, [handleResize]);

  return (
    <>
      <nav
        className={clsx(
          'stroke w-screen fixed overflow-hidden flex flex-row justify-between items-start',
          'text-center top-0 z-40 text-base overflow-visible',
          'bg-[#2F2629]',
          navHeightClass,
        )}
      >
        {/* Navigation Left */}
        <div
          className={clsx(
            'justify-start items-center pl-[12px]',
            navHeightClass,
            mWidth < mHeight ? 'w-[50vw]' : 'w-[25vw]',
          )}
        >

          {/* Logo */}
          <a name="navBtn" className={clsx('flex items-center', navHeightClass)} href={`${localePrefix}/home#0`}>
            <img
              src="/logo+text-landscape.svg"
              alt="logo"
              className="h-[44px] web:h-[50px] w-auto"
            />
          </a>
        </div>

        {/* Navigation Center */}
        <ul
          className={clsx(
            'w-[50vw] justify-center text-white',
            navHeightClass,
            'tablet:flex hidden',
          )}
        >
          <li className={`navBtn`}>
            <a
              name="navBtn"
              className={`navAncher font-medium flex justify-center items-center w-[120px] h-[40px] mx-6 ${
                active === '2' && 'active'
              }`}
              href={`${localePrefix}/home#2`}
            >
              {getLocale('nav.notice')}
            </a>
          </li>
          <li className={`navBtn`}>
            <a
              name="navBtn"
              className={`navAncher font-medium flex justify-center items-center w-[120px] h-[40px] mx-6 ${
                active === '3' && 'active'
              }`}
              href={`${localePrefix}/home#3`}
            >
              {getLocale('nav.worldview')}
            </a>
          </li>
          <li className={`navBtn`}>
            <a
              name="navBtn"
              className={`navAncher font-medium flex justify-center items-center w-[120px] h-[40px] mx-6 ${
                (active === '4' || pathname.startsWith(`${localePrefix}/stella`)) && 'active'
              }`}
              href={`${localePrefix}/home#4`}
            >
              {getLocale('nav.stella')}
            </a>
          </li>
          <li className={`navBtn`}>
            <a
              name="navBtn"
              className={`navAncher font-medium flex justify-center items-center w-[120px] h-[40px] mx-6 ${
                active === '5' && 'active'
              }`}
              href={`${localePrefix}/home#5`}
            >
              {getLocale('nav.media')}
            </a>
          </li>
          {/* ponytail: 날짜 상수 변경으로 공개 시점 조절 */}
          {new Date() >= new Date('2026-10-19T13:00:00+09:00') && (
            <li className={`navBtn`}>
              <a
                name="navBtn"
                className={`navAncher font-medium flex justify-center items-center w-[120px] h-[40px] mx-6 ${
                  active === '5' && 'active'
                }`}
                href={`/webtoon`}
              >
                {getLocale('nav.webtoon')}
              </a>
            </li>
          )}
        </ul>
        {/*
             strfeet
          => 1233245 
          => 1teet45 => steetrf =>rteetsf
          => 1ette45
        */}
        {/* Navigation Right */}
        <ul
          className={`flex flex-row pr-[12px] items-center justify-end ${navHeightClass} ${
            mWidth < mHeight ? 'w-[50vw]' : 'w-[25vw]'
          }`}
        >
          {/* Language Button */}
          <li>
            <LocaleSelect/>
          </li>
          {/* Youtube Button */}
          <li className={clsx('flex items-center justify-center', navItemSlotClass)}>
            <a
              href="https://www.youtube.com/channel/UCGyXcENDHN_C3POdU-BiZXg?sub_confirmation=1"
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
            >
              <div className="youtube w-[24px] h-[24px]" />
            </a>
          </li>
          {/* Twitter Button */}
          <li className={clsx('flex items-center justify-center', navItemSlotClass)}>
            <a
              href="https://twitter.com/intent/follow?original_referer=https%3A%2F%2Flistella.tv%2F&ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Efollow%7Ctwgr%5Elistella_on&screen_name=listella_on"
              target="_blank"
              rel="noreferrer"
              aria-label="X (Twitter)"
            >
              <div className="twitter w-[24px] h-[24px]" />
            </a>
          </li>
          {/* Share Button */}
          <li className={clsx('flex items-center justify-center', navItemSlotClass)}>
            <button onClick={handleToggleClick} aria-label="share">
              <div className="share w-[24px] h-[24px]" />
            </button>
          </li>
        </ul>
      </nav>

      {/* Share Box */}
      <div
        ref={shareRef}
        className={`share-box z-40 absolute flex flex-col justify-center right-0 mt-[60px] web:mt-[69px] w-[60px] web:w-[69px] ${
          click ? 'scale-100 h-[210px]' : 'scale-0 h-[420px]'
        }`}
      >
        <ul className="flex justify-center items-center m-1 division">
          <FacebookShareButton url={'https://listella.tv/'}>
            <div className="facebook w-[24px] h-[24px]" />
          </FacebookShareButton>
        </ul>
        <ul className="flex justify-center items-center m-1 division">
          <TwitterShareButton url={'버츄얼 라이버 모집 오디션 진행중!\n#리스텔라 #버추얼라이버모집\nhttps://listella.tv/'}>
            <div className="twitter w-[24px] h-[24px]" />
          </TwitterShareButton>
        </ul>
        <ul className="flex justify-center items-center m-1">
          <CopyToClipboard text={'버츄얼 라이버 모집 오디션 진행중!\nhttps://listella.tv/'} onCopy={notify}>
            <button aria-label="copy link">
              <div className="link w-[24px] h-[24px]" />
            </button>
          </CopyToClipboard>
        </ul>
      </div>
    </>
  );
}

